import {
	createAsyncThunk,
	createSlice,
	isAnyOf,
	nanoid,
} from '@reduxjs/toolkit'
import dayjs from 'dayjs'
import i18next from 'i18next'
import posthog from 'posthog-js'
import { updateInvoicesSimplified } from 'src/graphql/customMutations'
import * as mutations from 'src/graphql/mutations'
import * as queries from 'src/graphql/queries'
import { clientApi } from 'src/redux/api/graphql/clientApi'
import type { RootState, ThunkAppDispatch } from 'src/redux/store'
import type {
	ReduxBankTransactions,
	ReduxClients,
	ReduxCompany,
	ReduxInvoices,
	ReduxPreparePurchase,
	ReduxPurchase,
	ReduxRecapInfo,
} from 'src/types/apiType'
import type { ApiStatus } from 'src/types/miscType'
import { API, graphqlOperation } from 'src/utils/amplify/legacy'
import { getErrorMessage } from 'src/utils/error'
import {
	fetchBankTransactionsByTenantIdUtil,
	fetchPurchasesByCostlineIdUtil,
	fetchPurchasesByTenantIdUtil,
	getFetchFilter,
} from 'src/utils/fetchUtils'
import {
	getDueBasedInvoiceStatus,
	recomputeOriginalInvoiceStatus,
} from 'src/utils/invoiceUtils/paymentStatus'
import { makeErrorToast, makeSuccessToast, msg } from 'src/utils/makeToast'
import { captureExceptionAndThrow } from 'src/utils/posthogException'

type BankingState = {
	transactionsData: any
	banksData: any
	accountsData: any
	itemsData: any
	bankAccountsData: any
	currentTransaction: ReduxBankTransactions | null
	createOrUpdateSelectedAccountDetailsApiStatus: ApiStatus
	fetchTransactionsApiStatus: ApiStatus
	fetchTransactionsFromDBApiStatus: ApiStatus
	fetchItemsAndBankAndAccountsApiStatus: ApiStatus
	fetchBankAccountsInfoApiStatus: ApiStatus
	addBankAccountApiStatus: ApiStatus
	deleteItemApiStatus: ApiStatus
	matchInvoicesApiStatus: ApiStatus
	matchExportInvoicesApiStatus: ApiStatus
	matchTransactionsApiStatus: ApiStatus
	unmatchTransactionApiStatus: ApiStatus
	toggleTransactionsFlagApiStatus: ApiStatus
	addPettyCashInvoicesApiStatus: ApiStatus
	openMatchInvoicesDialogStatus: boolean
	openMatchExportInvoicesDialogStatus: boolean
	openMatchTransactionDialogStatus: boolean
	unmatchTransactionDialogStatus: boolean
	clientModalOpenStatus: boolean
	clientDeleteDialogOpenStatus: boolean
	viewSupplierInvoicesModalStatus: boolean
	pettyCashTransactionsInvoiceModalStatus: boolean
	currentClient: ReduxClients | null
	error?: string | null
}
const initialState: BankingState = {
	transactionsData: [],
	banksData: [],
	accountsData: [],
	itemsData: [],
	bankAccountsData: [],
	currentTransaction: null,
	createOrUpdateSelectedAccountDetailsApiStatus: 'idle',
	fetchTransactionsApiStatus: 'idle',
	fetchTransactionsFromDBApiStatus: 'idle',
	fetchItemsAndBankAndAccountsApiStatus: 'idle',
	fetchBankAccountsInfoApiStatus: 'idle',
	addBankAccountApiStatus: 'idle',
	deleteItemApiStatus: 'idle',
	matchInvoicesApiStatus: 'idle',
	matchExportInvoicesApiStatus: 'idle',
	matchTransactionsApiStatus: 'idle',
	unmatchTransactionApiStatus: 'idle',
	toggleTransactionsFlagApiStatus: 'idle',
	addPettyCashInvoicesApiStatus: 'idle',
	openMatchTransactionDialogStatus: false,
	openMatchInvoicesDialogStatus: false,
	openMatchExportInvoicesDialogStatus: false,
	unmatchTransactionDialogStatus: false,
	clientModalOpenStatus: false,
	clientDeleteDialogOpenStatus: false,
	viewSupplierInvoicesModalStatus: false,
	pettyCashTransactionsInvoiceModalStatus: false,
	currentClient: null,
	error: null,
}

export const fetchBankAccountsInfo = createAsyncThunk<
	any,
	undefined,
	{ state: RootState; rejectValue: string }
>(
	'banking/fetchBankAccountsInfo',
	async (arg, { getState, rejectWithValue }) => {
		try {
			const { auth } = getState()
			const userTableInfo = auth.userTableInfo
			const tenantId = userTableInfo?.currentCompanyTenantId
			if (!tenantId) {
				return rejectWithValue('Tenant ID is not set')
			}
			const {
				data: { getCompany },
			} = (await API.graphql(
				graphqlOperation(queries.getCompany, { id: tenantId })
			)) as {
				data: { getCompany: ReduxCompany }
			}

			if (!getCompany || !getCompany.isLoggedIntoBanking) {
				return []
			}

			const {
				data: {
					bankAccountInfoByTenantId: { items: bankAccountInfo },
				},
			} = (await API.graphql(
				graphqlOperation(queries.bankAccountInfoByTenantId, {
					tenantId: tenantId,
					filter: { activeFlag: { eq: true } },
				})
			)) as {
				data: { bankAccountInfoByTenantId: { items: any } }
			}

			return bankAccountInfo
		} catch (err: any) {
			console.error('Error fetching bank accounts info:', err)
			const normalizedError =
				err instanceof Error ? err : new Error(String(err))
			posthog.captureException('error_fetching_bank_accounts_info', {
				error: normalizedError,
			})
			return rejectWithValue(err)
		}
	}
)
export const fetchTransactionsFromDB = createAsyncThunk<
	any,
	{ startDate?: any; endDate?: any },
	{ state: RootState; rejectValue: string }
>(
	'banking/fetchTransactionsFromDB',
	async ({ startDate, endDate }, { getState, rejectWithValue }) => {
		try {
			const { auth } = getState()
			const userTableInfo = auth.userTableInfo
			const tenantId = userTableInfo?.currentCompanyTenantId
			if (!tenantId) {
				return rejectWithValue('Tenant ID is not set')
			}
			const {
				data: { getCompany },
			} = (await API.graphql(
				graphqlOperation(queries.getCompany, { id: tenantId })
			)) as {
				data: { getCompany: ReduxCompany }
			}

			if (!getCompany || !getCompany.isLoggedIntoBanking) {
				return []
			}

			const bankTransactions: any =
				await fetchBankTransactionsByTenantIdUtil(
					tenantId,
					startDate,
					endDate
				)

			return bankTransactions
		} catch (err: any) {
			console.error('Error fetching transactions from DB input:', {
				startDate,
				endDate,
			})
			console.error('Error fetching transactions from DB error:', err)
			const normalizedError =
				err instanceof Error ? err : new Error(String(err))
			posthog.captureException('error_fetching_transactions_from_db', {
				startDate,
				endDate,
				error: normalizedError,
			})
			return rejectWithValue(err)
		}
	}
)

export const fetchItemsAndBankAndAccounts = createAsyncThunk<
	any,
	undefined,
	{
		state: RootState
		dispatch: ThunkAppDispatch
		rejectValue: string
	}
>(
	'banking/fetchItemsAndBankAndAccounts',
	async (arg, { getState, dispatch, rejectWithValue }) => {
		try {
			const { auth } = getState()
			const tenantId = auth.userTableInfo?.currentCompanyTenantId
			if (!tenantId) {
				return rejectWithValue('Tenant ID is not set')
			}
			const {
				data: { getCompany },
			} = (await API.graphql(
				graphqlOperation(queries.getCompany, { id: tenantId })
			)) as {
				data: { getCompany: ReduxCompany }
			}

			if (!getCompany || !getCompany.isLoggedIntoBanking) {
				return { itemsArray: [], banksArray: [], accountsArray: [] }
			}

			const { data } = (await API.graphql(
				graphqlOperation(mutations.fetchItemsAndBankAndAccounts, {
					companyTenantId: tenantId,
				})
			)) as { data: any }
			return data?.fetchItemsAndBankAndAccounts
		} catch (err: any) {
			console.error('Error fetching items and bank and accounts:', err)
			const normalizedError =
				err instanceof Error ? err : new Error(String(err))
			posthog.captureException(
				'error_fetching_items_and_bank_and_accounts',
				{ error: normalizedError }
			)
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

export const fetchTransactionsPerAccount = createAsyncThunk<
	any,
	{ currentTenantId?: string },
	{
		state: RootState
		dispatch: ThunkAppDispatch
		rejectValue: string
	}
>(
	'banking/fetchTransactionsPerAccount',
	async (
		{ currentTenantId = null },
		{ getState, dispatch, rejectWithValue }
	) => {
		try {
			const { auth } = getState()
			const startDate =
				JSON.parse(
					window.localStorage.getItem('transactionsStartDate') ||
						'null'
				) ?? dayjs().startOf('year')
			const endDate =
				JSON.parse(
					window.localStorage.getItem('transactionsEndDate') || 'null'
				) ?? dayjs().startOf('year')
			let tenantId = auth.userTableInfo?.currentCompanyTenantId
			if (!tenantId) {
				return rejectWithValue('Tenant ID is not set')
			}
			if (!tenantId) {
				if (!currentTenantId) {
					captureExceptionAndThrow(new Error('No tenant Id'), {
						location: 'banking.fetchTransactionsPerAccount',
						authTenantId:
							auth.userTableInfo?.currentCompanyTenantId,
						currentTenantId,
						userId: auth.userTableInfo?.id,
					})
				} else {
					tenantId = currentTenantId
				}
			}
			const {
				data: { getCompany },
			} = (await API.graphql(
				graphqlOperation(queries.getCompany, { id: tenantId })
			)) as {
				data: { getCompany: ReduxCompany }
			}

			if (!getCompany || !getCompany.isLoggedIntoBanking) {
				return []
			}
			const {
				data: {
					fetchUpdatedTransactions: { status, isSuccessful },
				},
			} = (await API.graphql(
				graphqlOperation(mutations.fetchUpdatedTransactions, {
					companyTenantId: tenantId,
				})
			)) as {
				data: {
					fetchUpdatedTransactions: {
						status: string
						isSuccessful: boolean
					}
				}
			}
			if (isSuccessful) {
				console.log('Transactions successfully updated')
				dispatch(
					fetchTransactionsFromDB({
						startDate,
						endDate,
					})
				)
			} else {
				console.log('Something went wrong.Transactions not updated')
			}
		} catch (err: any) {
			console.error('Error fetching transactions per account input:', {
				currentTenantId,
			})
			console.error('Error fetching transactions per account error:', err)
			const normalizedError =
				err instanceof Error ? err : new Error(String(err))
			posthog.captureException(
				'error_fetching_transactions_per_account',
				{ currentTenantId, error: normalizedError }
			)
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

// Redirects to bridge apis add bank url
export const addBank = createAsyncThunk<
	void,
	undefined,
	{
		dispatch: ThunkAppDispatch
		state: RootState
		rejectValue: string
	}
>('banking/addBank', async (arg, { getState, dispatch, rejectWithValue }) => {
	try {
		const { auth } = getState()
		const tenantId = auth.userTableInfo?.currentCompanyTenantId
		if (!tenantId) {
			return rejectWithValue('Tenant ID is not set')
		}
		const {
			data: {
				addBank: { status: redirectUrl, isSuccessful },
			},
		} = (await API.graphql(
			graphqlOperation(mutations.addBank, {
				companyTenantId: tenantId,
			})
		)) as { data: { addBank: { status: string; isSuccessful: boolean } } }

		if (isSuccessful) {
			window.location.href = redirectUrl
		} else {
			makeErrorToast(msg)
		}
	} catch (err: any) {
		console.error('Error adding bank:', err)
		const normalizedError =
			err instanceof Error ? err : new Error(String(err))
		posthog.captureException('error_adding_bank', {
			error: normalizedError,
		})
		makeErrorToast(msg)
		return rejectWithValue(err)
	}
})
export const editItem = createAsyncThunk<
	void,
	number,
	{
		dispatch: ThunkAppDispatch
		state: RootState
		rejectValue: string
	}
>(
	'banking/editItem',
	async (itemId, { getState, dispatch, rejectWithValue }) => {
		try {
			const { auth } = getState()
			const tenantId = auth.userTableInfo?.currentCompanyTenantId
			if (!tenantId) {
				return rejectWithValue('Tenant ID is not set')
			}
			const {
				data: {
					editItem: { status: redirectUrl, isSuccessful },
				},
			} = (await API.graphql(
				graphqlOperation(mutations.editItem, {
					companyTenantId: tenantId,
					itemId,
				})
			)) as {
				data: { editItem: { status: string; isSuccessful: boolean } }
			}

			if (isSuccessful) {
				window.location.href = redirectUrl
			} else {
				makeErrorToast(msg)
			}
		} catch (err: any) {
			console.error('Error editing item input:', { itemId })
			console.error('Error editing item error:', err)
			const normalizedError =
				err instanceof Error ? err : new Error(String(err))
			posthog.captureException('error_editing_item', {
				itemId,
				error: normalizedError,
			})
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)
export const deleteItem = createAsyncThunk<
	void,
	number,
	{
		dispatch: ThunkAppDispatch
		state: RootState
		rejectValue: string
	}
>(
	'banking/deleteItem',
	async (itemId, { getState, dispatch, rejectWithValue }) => {
		try {
			const { auth } = getState()
			const tenantId = auth.userTableInfo?.currentCompanyTenantId
			if (!tenantId) {
				return rejectWithValue('Tenant ID is not set')
			}
			const {
				data: {
					deleteItem: { status: redirectUrl, isSuccessful },
				},
			} = (await API.graphql(
				graphqlOperation(mutations.deleteItem, {
					companyTenantId: tenantId,
					itemId,
				})
			)) as {
				data: { deleteItem: { status: string; isSuccessful: boolean } }
			}

			if (isSuccessful) {
				dispatch(fetchItemsAndBankAndAccounts())
			} else {
				makeErrorToast(msg)
			}
		} catch (err: any) {
			console.error('Error deleting item input:', { itemId })
			console.error('Error deleting item error:', err)
			const normalizedError =
				err instanceof Error ? err : new Error(String(err))
			posthog.captureException('error_deleting_item', {
				itemId,
				error: normalizedError,
			})
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)
export const redirectToSCA = createAsyncThunk<
	void,
	number,
	{
		dispatch: ThunkAppDispatch
		state: RootState
		rejectValue: string
	}
>(
	'banking/redirectToSCA',
	async (itemId, { getState, dispatch, rejectWithValue }) => {
		try {
			const { auth } = getState()
			const tenantId = auth.userTableInfo?.currentCompanyTenantId
			const {
				data: {
					redirectToSCA: { status: redirectUrl, isSuccessful },
				},
			} = (await API.graphql(
				graphqlOperation(mutations.redirectToSCA, {
					companyTenantId: tenantId,
					itemId,
				})
			)) as {
				data: {
					redirectToSCA: { status: string; isSuccessful: boolean }
				}
			}

			if (isSuccessful) {
				window.location.href = redirectUrl
			} else {
				makeErrorToast(msg)
			}
		} catch (err: any) {
			console.error('Error redirecting to SCA input:', { itemId })
			console.error('Error redirecting to SCA error:', err)
			const normalizedError =
				err instanceof Error ? err : new Error(String(err))
			posthog.captureException('error_redirecting_to_sca', {
				itemId,
				error: normalizedError,
			})
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)
export const validateProAccount = createAsyncThunk<
	void,
	undefined,
	{
		dispatch: ThunkAppDispatch
		state: RootState
		rejectValue: string
	}
>(
	'banking/validateProAccount',
	async (args, { getState, dispatch, rejectWithValue }) => {
		try {
			const { auth } = getState()
			const tenantId = auth.userTableInfo?.currentCompanyTenantId
			const {
				data: {
					validateProAccounts: { status: redirectUrl, isSuccessful },
				},
			} = (await API.graphql(
				graphqlOperation(mutations.validateProAccounts, {
					companyTenantId: tenantId,
				})
			)) as {
				data: {
					validateProAccounts: {
						status: string
						isSuccessful: boolean
					}
				}
			}

			if (isSuccessful) {
				window.location.href = redirectUrl
			} else {
				makeErrorToast(msg)
			}
		} catch (err: any) {
			console.error('Error validating pro account:', err)
			const normalizedError =
				err instanceof Error ? err : new Error(String(err))
			posthog.captureException('error_validating_pro_account', {
				error: normalizedError,
			})
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)
export const createOrUpdateSelectedAccountDetails = createAsyncThunk<
	void,
	any,
	{
		dispatch: ThunkAppDispatch
		state: RootState
		rejectValue: string
	}
>(
	'banking/createOrUpdateSelectedAccountDetails',
	async (selectedAccounts, { getState, dispatch, rejectWithValue }) => {
		try {
			const newSelectedArray = []
			for (const acc of selectedAccounts) {
				const newObj = {
					id: acc.id,
					name: acc.name,
					balance: acc.balance,
					status: acc.status,
					status_code_info: acc.status_code_info,
					status_code_description: acc.status_code_description,
					updated_at: acc.updated_at,
					type: acc.type,
					currency_code: acc.currency_code,
					item_id: acc.item_id,
					bank_id: acc.bank_id,
					loan_details: acc.loan_details,
					savings_details: acc.savings_details,
					is_pro: acc.is_pro,
					iban: acc.iban,
					isSelected: acc.isSelected,
				}
				newSelectedArray.push(newObj)
			}
			const { auth } = getState()
			const tenantId = auth.userTableInfo?.currentCompanyTenantId
			if (!tenantId) {
				return rejectWithValue('Tenant ID is not set')
			}
			const {
				data: {
					createOrUpdateSelectedAccountDetails: {
						status,
						isSuccessful,
					},
				},
			} = (await API.graphql(
				graphqlOperation(
					mutations.createOrUpdateSelectedAccountDetails,
					{
						companyTenantId: tenantId,
						selectedAccounts: newSelectedArray,
					}
				)
			)) as {
				data: {
					createOrUpdateSelectedAccountDetails: {
						status: string
						isSuccessful: boolean
					}
				}
			}
			if (isSuccessful) {
				dispatch(fetchBankAccountsInfo())
				makeSuccessToast('Successfully updated')
			} else {
				makeErrorToast('Something unexpected happened')
			}
		} catch (err: any) {
			console.error(
				'Error creating or updating selected account details input:',
				{ selectedAccounts }
			)
			console.error(
				'Error creating or updating selected account details error:',
				err
			)
			const normalizedError =
				err instanceof Error ? err : new Error(String(err))
			posthog.captureException(
				'error_creating_or_updating_selected_account_details',
				{ selectedAccounts, error: normalizedError }
			)
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)
export const matchInvoices = createAsyncThunk<
	void,
	{ invoices: any; transactionId: any },
	{
		dispatch: ThunkAppDispatch
		state: RootState
		rejectValue: string
	}
>(
	'banking/matchInvoices',
	async (
		{ invoices, transactionId },
		{ getState, dispatch, rejectWithValue }
	) => {
		try {
			const { auth } = getState()
			const tenantId = auth.userTableInfo?.currentCompanyTenantId
			const {
				data: { getBankTransactions },
			} = (await API.graphql(
				graphqlOperation(queries.getBankTransactions, {
					id: transactionId,
				})
			)) as {
				data: { getBankTransactions: ReduxBankTransactions }
			}
			const startDate =
				JSON.parse(
					window.localStorage.getItem('transactionsStartDate') ||
						'null'
				) ?? dayjs().startOf('year')
			const endDate =
				JSON.parse(
					window.localStorage.getItem('transactionsEndDate') || 'null'
				) ?? dayjs().startOf('year')
			const transactionCurrency = getBankTransactions.currency_code
			const tBanking = i18next.getFixedT(null, 'banking')
			if (
				invoices.some(
					(inv: ReduxInvoices) =>
						inv.clientCurrency !== transactionCurrency
				)
			) {
				makeErrorToast(tBanking('toasts.currencyMismatch'))
				return rejectWithValue(tBanking('toasts.currencyMismatch'))
			}
			const creditTotal = invoices
				.filter((i: ReduxInvoices) => i.invoiceType === 'credit')
				.reduce(
					(a: any, b: any) =>
						a + Number.parseFloat(b.amountAfterTax || 0),
					0
				)
			const invoiceTotal = invoices
				.filter((i: ReduxInvoices) => i.invoiceType !== 'credit')
				.reduce(
					(a: any, b: any) => a + Number.parseFloat(b.amountAfterTax),
					0
				)

			const totalAmount = (invoiceTotal - creditTotal).toFixed(2)

			if (getBankTransactions.amount !== Number.parseFloat(totalAmount)) {
				makeErrorToast(tBanking('toasts.totalMismatch'))
				return rejectWithValue(tBanking('toasts.totalMismatch'))
			}
			const uniqueId = nanoid()
			const affectedOriginalInvoiceIds = new Set<string>()
			for (const inv of invoices) {
				const updateInvObject = {
					id: inv.id,
					paymentStatus: 'Paid',
					paidDate: dayjs(getBankTransactions.date).toISOString(),
					uniqueTransactionId: uniqueId,
				}
				await API.graphql(
					graphqlOperation(updateInvoicesSimplified, {
						input: updateInvObject,
					})
				)

				if (inv.invoiceType === 'credit' && inv.originalInvoiceId) {
					affectedOriginalInvoiceIds.add(inv.originalInvoiceId)
				}
			}
			if (tenantId) {
				for (const originalInvoiceId of affectedOriginalInvoiceIds) {
					await recomputeOriginalInvoiceStatus({
						tenantId,
						originalInvoiceId,
					})
				}
			}
			const updateTransactionObject = {
				id: getBankTransactions.id,
				uniqueTransactionId: uniqueId,
				status: 'Matched',
			}
			await API.graphql(
				graphqlOperation(mutations.updateBankTransactions, {
					input: updateTransactionObject,
				})
			)
			dispatch(fetchTransactionsFromDB({ startDate, endDate }))
			makeSuccessToast(tBanking('toasts.matchSuccess'))
		} catch (err: any) {
			console.error('Error matching invoices input:', {
				invoices,
				transactionId,
			})
			console.error('Error matching invoices error', err)
			const normalizedError =
				err instanceof Error ? err : new Error(String(err))
			posthog.captureException('error_matching_invoices', {
				invoices,
				transactionId,
				error: normalizedError,
			})
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

export const matchExportInvoices = createAsyncThunk<
	void,
	{ invoices: any; transactionId: any },
	{
		dispatch: ThunkAppDispatch
		state: RootState
		rejectValue: string
	}
>(
	'banking/matchExportInvoices',
	async (
		{ invoices, transactionId },
		{ getState, dispatch, rejectWithValue }
	) => {
		try {
			const { auth } = getState()
			const tenantId = auth.userTableInfo?.currentCompanyTenantId
			if (!tenantId) {
				return rejectWithValue('Tenant ID is not set')
			}
			const tBanking = i18next.getFixedT(null, 'banking')
			const startDate =
				JSON.parse(
					window.localStorage.getItem('transactionsStartDate') ||
						'null'
				) ?? dayjs().startOf('year')
			const endDate =
				JSON.parse(
					window.localStorage.getItem('transactionsEndDate') || 'null'
				) ?? dayjs().startOf('year')
			const filter = getFetchFilter(auth.userTableInfo)
			const {
				data: { getBankTransactions },
			} = (await API.graphql(
				graphqlOperation(queries.getBankTransactions, {
					id: transactionId,
				})
			)) as {
				data: { getBankTransactions: ReduxBankTransactions }
			}
			const transactionCurrency = getBankTransactions.currency_code
			if (
				invoices.some(
					(inv: ReduxPreparePurchase) =>
						inv.currency !== transactionCurrency
				)
			) {
				makeErrorToast(tBanking('toasts.currencyMismatch'))
				return rejectWithValue(tBanking('toasts.currencyMismatch'))
			}
			const totalAmount = invoices.reduce(
				(a: any, b: any) => a + Number.parseFloat(b.totalAmount || 0),
				0
			)
			const absBankTransactionAmt = Math.abs(
				getBankTransactions?.amount || 0
			)
			const absTotalAmount = Math.abs(totalAmount)
			if (absBankTransactionAmt !== absTotalAmount) {
				makeErrorToast(tBanking('toasts.totalMismatch'))
				return rejectWithValue(tBanking('toasts.totalMismatch'))
			}
			const uniqueId = nanoid()
			for (const items of invoices) {
				const updatePreparePurchase: Partial<ReduxPreparePurchase> = {
					id: items.id,
					paymentStatus: 'Paid',
					preparePurchasePaidDate: dayjs(
						getBankTransactions.date
					).toISOString(),
					uniqueTransactionId: uniqueId,
				}
				await API.graphql(
					graphqlOperation(mutations.updatePreparePurchase, {
						input: updatePreparePurchase,
					})
				)

				if (
					items?.costlineId &&
					(items.type === 'costline' || items.type === 'newCostline')
				) {
					for (const costlineId of items.costlineId) {
						const currentPurchase: ReduxPurchase[] =
							await fetchPurchasesByCostlineIdUtil({
								recapCostlineId: costlineId,
								filter,
							})
						const updatedPurchase: Partial<ReduxPurchase> = {
							id: currentPurchase[0].id,
							paymentStatus: 'Paid',
							purchasePaidDate: dayjs(
								getBankTransactions.date
							).toISOString(),
						}

						await API.graphql(
							graphqlOperation(mutations.updatePurchase, {
								input: updatedPurchase,
							})
						)

						if (
							currentPurchase[0].purchaseType === 'costline' ||
							currentPurchase[0].purchaseType === 'newCostline'
						) {
							const {
								data: { getRecapInfo },
							} = (await API.graphql(
								graphqlOperation(queries.getRecapInfo, {
									id: currentPurchase[0].recapCostlineId,
								})
							)) as { data: { getRecapInfo: ReduxRecapInfo } }

							const updatedRecap: Partial<ReduxRecapInfo> = {
								id: getRecapInfo.id,
								paymentStatus: 'Paid',
							}

							await API.graphql(
								graphqlOperation(mutations.updateRecapInfo, {
									input: updatedRecap,
								})
							)
						}
					}
				} else {
					const purchasesData = await fetchPurchasesByTenantIdUtil({
						tenantId,
					})
					const filterPurchases = purchasesData.filter(
						(purchase) => purchase.invoiceKey === items.invoiceKey
					)
					if (filterPurchases.length > 0) {
						for (const individualPurchase of filterPurchases) {
							const updatedPurchase: Partial<ReduxPurchase> = {
								id: individualPurchase.id,
								paymentStatus: 'Paid',
								purchasePaidDate: dayjs(
									getBankTransactions.date
								).toISOString(),
							}

							await API.graphql(
								graphqlOperation(mutations.updatePurchase, {
									input: updatedPurchase,
								})
							)
						}
					}
				}
			}
			const updateTransactionObject = {
				id: getBankTransactions.id,
				uniqueTransactionId: uniqueId,
				status: 'Matched',
			}
			await API.graphql(
				graphqlOperation(mutations.updateBankTransactions, {
					input: updateTransactionObject,
				})
			)
			dispatch(
				fetchTransactionsFromDB({
					startDate,
					endDate,
				})
			)
			makeSuccessToast(tBanking('toasts.matchSuccess'))
		} catch (err: any) {
			console.error('Error matching export invoices input:', {
				invoices,
				transactionId,
			})
			console.error('Error matching export invoices error:', err)
			const normalizedError =
				err instanceof Error ? err : new Error(String(err))
			posthog.captureException('error_matching_export_invoices', {
				invoices,
				transactionId,
				error: normalizedError,
			})
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)
export const matchMiscellaneous = createAsyncThunk<
	void,
	{ data: any; transactionId: any },
	{
		dispatch: ThunkAppDispatch
		state: RootState
		rejectValue: string
	}
>(
	'banking/matchMiscellaneous',
	async (
		{ data, transactionId },
		{ getState, dispatch, rejectWithValue }
	) => {
		try {
			const {
				data: { getBankTransactions },
			} = (await API.graphql(
				graphqlOperation(queries.getBankTransactions, {
					id: transactionId,
				})
			)) as {
				data: { getBankTransactions: ReduxBankTransactions }
			}
			const tBanking = i18next.getFixedT(null, 'banking')
			const startDate =
				JSON.parse(
					window.localStorage.getItem('transactionsStartDate') ||
						'null'
				) ?? dayjs().startOf('year')
			const endDate =
				JSON.parse(
					window.localStorage.getItem('transactionsEndDate') || 'null'
				) ?? dayjs().startOf('year')
			const updateTransactionObject = {
				id: getBankTransactions.id,
				miscAccNo: data.miscAccNo,
				status: 'Matched',
			}
			await API.graphql(
				graphqlOperation(mutations.updateBankTransactions, {
					input: updateTransactionObject,
				})
			)
			dispatch(fetchTransactionsFromDB({ startDate, endDate }))
			makeSuccessToast(tBanking('toasts.matchSuccess'))
		} catch (err: any) {
			console.error('Error matching miscellaneous input:', {
				data,
				transactionId,
			})
			console.error('Error matching miscellaneous error:', err)
			const normalizedError =
				err instanceof Error ? err : new Error(String(err))
			posthog.captureException('error_matching_miscellaneous', {
				data,
				transactionId,
				error: normalizedError,
			})
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)

export const unmatchTransaction = createAsyncThunk<
	void,
	{ transactionId: any },
	{
		dispatch: ThunkAppDispatch
		state: RootState
		rejectValue: string
	}
>(
	'banking/unmatchTransaction',
	async ({ transactionId }, { getState, dispatch, rejectWithValue }) => {
		try {
			const { auth } = getState()
			const tenantId = auth.userTableInfo?.currentCompanyTenantId
			const filter = getFetchFilter(auth.userTableInfo)
			const tBanking = i18next.getFixedT(null, 'banking')
			const startDate =
				JSON.parse(
					window.localStorage.getItem('transactionsStartDate') ||
						'null'
				) ?? dayjs().startOf('year')
			const endDate =
				JSON.parse(
					window.localStorage.getItem('transactionsEndDate') || 'null'
				) ?? dayjs().startOf('year')
			const {
				data: { getBankTransactions },
			} = (await API.graphql(
				graphqlOperation(queries.getBankTransactions, {
					id: transactionId,
				})
			)) as {
				data: { getBankTransactions: ReduxBankTransactions }
			}

			if (!getBankTransactions?.miscAccNo) {
				if (getBankTransactions?.transactionType === 'revenue') {
					const invArray = getBankTransactions?.invoices?.items || []
					const affectedOriginalInvoiceIds = new Set<string>()
					if (invArray.length > 0) {
						for (let i = 0; i < invArray?.length; i++) {
							const invId = invArray[i]?.id
							const {
								data: { getInvoices },
							} = (await API.graphql(
								graphqlOperation(queries.getInvoices, {
									id: invId,
								})
							)) as {
								data: { getInvoices: ReduxInvoices }
							}
							const updateInvObject = {
								id: getInvoices.id,
								paymentStatus: getDueBasedInvoiceStatus(
									getInvoices.invoiceDueDate
								),
								uniqueTransactionId: null,
								paidDate: null,
							}

							await API.graphql(
								graphqlOperation(updateInvoicesSimplified, {
									input: updateInvObject,
								})
							)

							if (
								getInvoices.invoiceType === 'credit' &&
								getInvoices.originalInvoiceId
							) {
								affectedOriginalInvoiceIds.add(
									getInvoices.originalInvoiceId
								)
							}
						}
					}

					if (tenantId) {
						for (const originalInvoiceId of affectedOriginalInvoiceIds) {
							await recomputeOriginalInvoiceStatus({
								tenantId,
								originalInvoiceId,
							})
						}
					}
				} else if (getBankTransactions?.transactionType === 'cost') {
					const preparePurchaseArray =
						getBankTransactions?.preparePurchase?.items || []
					if (preparePurchaseArray.length > 0) {
						for (let i = 0; i < preparePurchaseArray?.length; i++) {
							const prepId = preparePurchaseArray[i]?.id
							const {
								data: { getPreparePurchase: items },
							} = (await API.graphql(
								graphqlOperation(queries.getPreparePurchase, {
									id: prepId,
								})
							)) as {
								data: {
									getPreparePurchase: ReduxPreparePurchase
								}
							}
							let newPaymentStatus = 'Pending'
							if (
								dayjs(items.paymentDate).diff(dayjs(), 'day') >=
								0
							) {
								newPaymentStatus = 'Pending'
							} else if (
								dayjs(items.paymentDate).diff(dayjs(), 'day') <
								0
							) {
								newPaymentStatus = 'Overdue'
							}
							let updatePreparePurchase: Partial<ReduxPreparePurchase> =
								{
									id: items.id,
									paymentStatus: newPaymentStatus,
									preparePurchasePaidDate: null,
									uniqueTransactionId: null,
								}
							if (
								items?.type ===
									'transactionsPettyCashCostline' ||
								items?.type === 'transactionsPettyCashOverhead'
							) {
								updatePreparePurchase = {
									id: items.id,
									activeFlag: false,
								}
							}
							await API.graphql(
								graphqlOperation(
									mutations.updatePreparePurchase,
									{ input: updatePreparePurchase }
								)
							)
							if (
								items.type !==
									'transactionsPettyCashCostline' &&
								items.type !== 'transactionsPettyCashOverhead'
							) {
								if (
									items?.costlineId &&
									(items.type === 'costline' ||
										items.type === 'newCostline')
								) {
									for (const costlineId of items.costlineId) {
										const currentPurchase: ReduxPurchase[] =
											await fetchPurchasesByCostlineIdUtil(
												{
													recapCostlineId: costlineId,
													filter,
												}
											)
										const updatedPurchase: Partial<ReduxPurchase> =
											{
												id: currentPurchase[0].id,
												paymentStatus: newPaymentStatus,
												purchasePaidDate: null,
											}

										await API.graphql(
											graphqlOperation(
												mutations.updatePurchase,
												{ input: updatedPurchase }
											)
										)

										if (
											currentPurchase[0].purchaseType ===
												'costline' ||
											currentPurchase[0].purchaseType ===
												'newCostline'
										) {
											const {
												data: { getRecapInfo },
											} = (await API.graphql(
												graphqlOperation(
													queries.getRecapInfo,
													{
														id: currentPurchase[0]
															.recapCostlineId,
													}
												)
											)) as {
												data: {
													getRecapInfo: ReduxRecapInfo
												}
											}

											const updatedRecap: Partial<ReduxRecapInfo> =
												{
													id: getRecapInfo.id,
													paymentStatus:
														newPaymentStatus,
												}

											await API.graphql(
												graphqlOperation(
													mutations.updateRecapInfo,
													{ input: updatedRecap }
												)
											)
										}
									}
								} else {
									const purchasesData =
										await fetchPurchasesByTenantIdUtil({
											tenantId,
										})
									const filterPurchases =
										purchasesData.filter(
											(purchase) =>
												purchase.invoiceKey ===
												items.invoiceKey
										)
									if (filterPurchases.length > 0) {
										for (const individualPurchase of filterPurchases) {
											const updatedPurchase: Partial<ReduxPurchase> =
												{
													id: individualPurchase.id,
													paymentStatus:
														newPaymentStatus,
													purchasePaidDate: null,
												}

											await API.graphql(
												graphqlOperation(
													mutations.updatePurchase,
													{
														input: updatedPurchase,
													}
												)
											)
										}
									}
								}
							}
						}
					}
				}
			}

			const updateTransactionObject = {
				id: getBankTransactions.id,
				miscAccNo: null,
				status: 'Pending',
				uniqueTransactionId: null,
			}
			await API.graphql(
				graphqlOperation(mutations.updateBankTransactions, {
					input: updateTransactionObject,
				})
			)
			dispatch(fetchTransactionsFromDB({ startDate, endDate }))
			makeSuccessToast(tBanking('toasts.unmatchSuccess'))
		} catch (err: any) {
			console.error('Error unmatching transaction input:', {
				transactionId,
			})
			console.error('Error unmatching transaction error:', err)
			const normalizedError =
				err instanceof Error ? err : new Error(String(err))
			posthog.captureException('error_unmatching_transaction', {
				transactionId,
				error: normalizedError,
			})
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)
export const toggleTransactionsFlag = createAsyncThunk<
	void,
	{ transactionId: string },
	{ dispatch: ThunkAppDispatch; state: RootState; rejectValue: string }
>(
	'banking/toggleTransactionsFlag',
	async ({ transactionId }, { dispatch, getState, rejectWithValue }) => {
		const { auth } = getState()
		const tenantId = auth.userTableInfo?.currentCompanyTenantId
		if (!tenantId) {
			return rejectWithValue('Tenant ID is not set')
		}
		try {
			if (!transactionId) return
			const startDate =
				JSON.parse(
					window.localStorage.getItem('transactionsStartDate') ||
						'null'
				) ?? dayjs().startOf('year')
			const endDate =
				JSON.parse(
					window.localStorage.getItem('transactionsEndDate') || 'null'
				) ?? dayjs().startOf('year')
			const {
				data: { getBankTransactions: items },
			} = (await API.graphql(
				graphqlOperation(queries.getBankTransactions, {
					id: transactionId,
				})
			)) as { data: { getBankTransactions: ReduxBankTransactions } }
			const updObj = {
				id: items.id,
				flag: !items?.flag,
			}
			await API.graphql(
				graphqlOperation(mutations.updateBankTransactions, {
					input: updObj,
				})
			)
			// dispatch(fetchTransactionsFromDB({ startDate, endDate }))
		} catch (err: any) {
			console.error('Error toggling transactions flag input:', {
				transactionId,
			})
			console.error('Error toggling transactions flag error:', err)
			const normalizedError =
				err instanceof Error ? err : new Error(String(err))
			posthog.captureException('error_toggling_transactions_flag', {
				transactionId,
				error: normalizedError,
			})
			makeErrorToast(msg)
			return rejectWithValue(err)
		}
	}
)
export const addPettyCashInvoices = createAsyncThunk<
	void,
	{ preparePurchaseData: any; currentTransactionId: any },
	{ dispatch: ThunkAppDispatch; state: RootState; rejectValue: string }
>(
	'banking/addPettyCashInvoices',
	async (
		{ preparePurchaseData, currentTransactionId },
		{ dispatch, getState, rejectWithValue }
	) => {
		const { auth } = getState()
		const tenantId = auth?.userTableInfo?.currentCompanyTenantId
		const userName = auth?.userTableInfo?.email
		if (!tenantId) {
			return rejectWithValue('Tenant ID is not set')
		}
		let errorMessage = msg
		const tBanking = i18next.getFixedT(null, 'banking')
		try {
			if (!preparePurchaseData) return
			console.log('preparePurchaseData: ', preparePurchaseData)
			const startDate =
				JSON.parse(
					window.localStorage.getItem('transactionsStartDate') ||
						'null'
				) ?? dayjs().startOf('year')
			const endDate =
				JSON.parse(
					window.localStorage.getItem('transactionsEndDate') || 'null'
				) ?? dayjs().startOf('year')
			const {
				data: {
					addPettyCashInvoice: { status, isSuccessful },
				},
			} = (await API.graphql(
				graphqlOperation(mutations.addPettyCashInvoice, {
					companyTenantId: tenantId,
					userName,
					currentTransactionId,
					pettyCashData: preparePurchaseData,
				})
			)) as {
				data: {
					addPettyCashInvoice: {
						status: string
						isSuccessful: boolean
					}
				}
			}
			if (isSuccessful) {
				dispatch(fetchTransactionsFromDB({ startDate, endDate }))
				makeSuccessToast(tBanking('toasts.pettyCashInvoiceCreated'))
			} else {
				if (status !== 'Failed') {
					errorMessage = status
				}
				captureExceptionAndThrow(new Error(status), {
					location: 'banking.addPettyCashInvoices',
					status,
					currentTransactionId,
					companyTenantId: tenantId,
				})
			}
		} catch (err: any) {
			const errorMessage = getErrorMessage(err)
			console.error('Error adding petty cash invoices input:', {
				preparePurchaseData,
				currentTransactionId,
			})
			console.error('Error adding petty cash invoices error:', err)
			console.error(
				'errorMessage for addPettyCashInvoices: ',
				errorMessage
			)
			const normalizedError =
				err instanceof Error ? err : new Error(String(err))
			posthog.captureException('error_adding_petty_cash_invoices', {
				preparePurchaseData,
				currentTransactionId,
				error: normalizedError,
			})
			makeErrorToast(`${errorMessage}`)
			return rejectWithValue(err)
		}
	}
)

export const bankingSlice = createSlice({
	name: 'banking',
	initialState,
	reducers: {
		clearTransactionsData: (state) => {
			state.transactionsData = []
		},
		setOpenMatchInvoicesDialogStatus: (state, action) => {
			state.openMatchInvoicesDialogStatus = action.payload
		},
		setOpenMatchExportInvoicesDialogStatus: (state, action) => {
			state.openMatchExportInvoicesDialogStatus = action.payload
		},
		setOpenMatchTransactionsDialogStatus: (state, action) => {
			state.openMatchTransactionDialogStatus = action.payload
		},
		setUnmatchTransactionDialogStatus: (state, action) => {
			state.unmatchTransactionDialogStatus = action.payload
		},
		setCurrentTransaction: (state, action) => {
			state.currentTransaction = action.payload
		},
		setViewSupplierInvoicesModalStatus: (state, action) => {
			state.viewSupplierInvoicesModalStatus = action.payload
		},
		setPettyCashTransactionsInvoiceModalStatus: (state, action) => {
			state.pettyCashTransactionsInvoiceModalStatus = action.payload
		},
	},
	extraReducers: (builder) => {
		builder.addCase(
			createOrUpdateSelectedAccountDetails.pending,
			(state, action) => {
				state.createOrUpdateSelectedAccountDetailsApiStatus = 'loading'
			}
		)
		builder.addCase(
			createOrUpdateSelectedAccountDetails.fulfilled,
			(state, action) => {
				state.createOrUpdateSelectedAccountDetailsApiStatus =
					'succeeded'
				state.bankAccountsData = action.payload
			}
		)
		builder.addCase(
			createOrUpdateSelectedAccountDetails.rejected,
			(state, action) => {
				state.createOrUpdateSelectedAccountDetailsApiStatus = 'failed'
				state.error = action.error.message
			}
		)
		builder.addCase(fetchBankAccountsInfo.pending, (state, action) => {
			state.fetchBankAccountsInfoApiStatus = 'loading'
		})
		builder.addCase(fetchBankAccountsInfo.fulfilled, (state, action) => {
			state.fetchBankAccountsInfoApiStatus = 'succeeded'
			state.bankAccountsData = action.payload
		})
		builder.addCase(fetchBankAccountsInfo.rejected, (state, action) => {
			state.fetchBankAccountsInfoApiStatus = 'failed'
			state.error = action.error.message
		})
		builder.addCase(fetchTransactionsFromDB.pending, (state, action) => {
			state.fetchTransactionsFromDBApiStatus = 'loading'
		})
		builder.addCase(fetchTransactionsFromDB.fulfilled, (state, action) => {
			state.fetchTransactionsFromDBApiStatus = 'succeeded'
			state.transactionsData = action.payload
		})
		builder.addCase(fetchTransactionsFromDB.rejected, (state, action) => {
			state.fetchTransactionsFromDBApiStatus = 'failed'
			state.error = action.error.message
		})
		builder.addCase(
			fetchItemsAndBankAndAccounts.pending,
			(state, action) => {
				state.fetchItemsAndBankAndAccountsApiStatus = 'loading'
			}
		)
		builder.addCase(
			fetchItemsAndBankAndAccounts.fulfilled,
			(state, action) => {
				state.fetchItemsAndBankAndAccountsApiStatus = 'succeeded'
				state.itemsData = action.payload.itemsArray
				state.accountsData = action.payload.accountsArray
				state.banksData = action.payload.banksArray
			}
		)
		builder.addCase(
			fetchItemsAndBankAndAccounts.rejected,
			(state, action) => {
				state.fetchItemsAndBankAndAccountsApiStatus = 'failed'
				state.error = action.error.message
			}
		)
		builder.addCase(
			fetchTransactionsPerAccount.pending,
			(state, action) => {
				state.fetchTransactionsApiStatus = 'loading'
			}
		)
		builder.addCase(
			fetchTransactionsPerAccount.fulfilled,
			(state, action) => {
				state.fetchTransactionsApiStatus = 'succeeded'
			}
		)
		builder.addCase(
			fetchTransactionsPerAccount.rejected,
			(state, action) => {
				state.fetchTransactionsApiStatus = 'failed'
				state.error = action.error.message
			}
		)
		builder.addCase(addBank.pending, (state, action) => {
			state.addBankAccountApiStatus = 'loading'
		})
		builder.addCase(addBank.fulfilled, (state, action) => {
			state.addBankAccountApiStatus = 'succeeded'
			state.clientModalOpenStatus = false
		})
		builder.addCase(addBank.rejected, (state, action) => {
			state.addBankAccountApiStatus = 'failed'
			state.clientModalOpenStatus = false
			state.error = action.error.message
		})
		builder.addCase(deleteItem.pending, (state, action) => {
			state.deleteItemApiStatus = 'loading'
		})
		builder.addCase(deleteItem.fulfilled, (state, action) => {
			state.deleteItemApiStatus = 'succeeded'
		})
		builder.addCase(deleteItem.rejected, (state, action) => {
			state.deleteItemApiStatus = 'failed'
			state.error = action.error.message
		})
		builder.addCase(matchInvoices.pending, (state, action) => {
			state.matchInvoicesApiStatus = 'loading'
			state.matchTransactionsApiStatus = 'loading'
		})
		builder.addCase(matchInvoices.fulfilled, (state, action) => {
			state.matchInvoicesApiStatus = 'succeeded'
			state.matchTransactionsApiStatus = 'succeeded'
			state.openMatchInvoicesDialogStatus = false
			state.openMatchTransactionDialogStatus = false
		})
		builder.addCase(matchInvoices.rejected, (state, action) => {
			state.matchInvoicesApiStatus = 'failed'
			state.matchTransactionsApiStatus = 'failed'
			state.error = action.error.message
		})
		builder.addCase(matchExportInvoices.pending, (state, action) => {
			state.matchExportInvoicesApiStatus = 'loading'
			state.matchTransactionsApiStatus = 'loading'
		})
		builder.addCase(matchExportInvoices.fulfilled, (state, action) => {
			state.matchExportInvoicesApiStatus = 'succeeded'
			state.matchTransactionsApiStatus = 'succeeded'
			state.openMatchExportInvoicesDialogStatus = false
			state.openMatchTransactionDialogStatus = false
		})
		builder.addCase(matchExportInvoices.rejected, (state, action) => {
			state.matchExportInvoicesApiStatus = 'failed'
			state.matchTransactionsApiStatus = 'failed'
			state.error = action.error.message
		})
		builder.addCase(matchMiscellaneous.pending, (state, action) => {
			state.matchTransactionsApiStatus = 'loading'
		})
		builder.addCase(matchMiscellaneous.fulfilled, (state, action) => {
			state.matchTransactionsApiStatus = 'succeeded'
			state.openMatchTransactionDialogStatus = false
		})
		builder.addCase(matchMiscellaneous.rejected, (state, action) => {
			state.matchTransactionsApiStatus = 'failed'
			state.error = action.error.message
		})
		builder.addCase(unmatchTransaction.pending, (state, action) => {
			state.unmatchTransactionApiStatus = 'loading'
		})
		builder.addCase(unmatchTransaction.fulfilled, (state, action) => {
			state.unmatchTransactionApiStatus = 'succeeded'
			state.unmatchTransactionDialogStatus = false
		})
		builder.addCase(unmatchTransaction.rejected, (state, action) => {
			state.unmatchTransactionApiStatus = 'failed'
			state.error = action.error.message
		})

		builder.addCase(toggleTransactionsFlag.pending, (state, action) => {
			state.toggleTransactionsFlagApiStatus = 'loading'
		})
		builder.addCase(toggleTransactionsFlag.fulfilled, (state, action) => {
			state.toggleTransactionsFlagApiStatus = 'succeeded'
		})
		builder.addCase(toggleTransactionsFlag.rejected, (state, action) => {
			state.toggleTransactionsFlagApiStatus = 'failed'
			state.error = action.error.message
		})
		builder.addCase(addPettyCashInvoices.pending, (state, action) => {
			state.addPettyCashInvoicesApiStatus = 'loading'
		})
		builder.addCase(addPettyCashInvoices.fulfilled, (state, action) => {
			state.addPettyCashInvoicesApiStatus = 'succeeded'
			state.pettyCashTransactionsInvoiceModalStatus = false
		})
		builder.addCase(addPettyCashInvoices.rejected, (state, action) => {
			state.addPettyCashInvoicesApiStatus = 'failed'
			state.error = action.error.message
		})

		builder.addMatcher(
			isAnyOf(
				clientApi.endpoints.updateClientById.matchFulfilled,
				clientApi.endpoints.createClientById.matchFulfilled
			),
			(state, { payload }) => {
				state.clientModalOpenStatus = false
			}
		)
		builder.addMatcher(
			isAnyOf(
				clientApi.endpoints.deleteClientById.matchFulfilled
				// clientApi.endpoints.deleteClientById.matchRejected
			),
			(state, { payload }) => {
				state.clientDeleteDialogOpenStatus = false
			}
		)
		builder.addMatcher(
			isAnyOf(clientApi.endpoints.deleteClientById.matchRejected),
			(state, { payload, error }) => {
				state.clientDeleteDialogOpenStatus = false
				console.log('error: ', error)
			}
		)
	},
})

export const {
	clearTransactionsData,
	setOpenMatchInvoicesDialogStatus,
	setOpenMatchExportInvoicesDialogStatus,
	setOpenMatchTransactionsDialogStatus,
	setUnmatchTransactionDialogStatus,
	setCurrentTransaction,
	setViewSupplierInvoicesModalStatus,
	setPettyCashTransactionsInvoiceModalStatus,
} = bankingSlice.actions

export default bankingSlice.reducer
